'use client';

import { ArrowDropDown } from "@mui/icons-material";
import { Box, Button, Menu, MenuItem, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";

const DATE_PRESETS = [
  "Today",
  "Yesterday",
  "This Week",
  "This Month",
  "This Quarter",
  "This Year",
  "Previous Week",
  "Previous Month",
  "Previous Quarter",
  "Previous Year",
  "Custom",
];

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function getRangeForPreset(preset) {
  const today = new Date();
  const day = today.getDay();

  const monday = new Date(today);
  monday.setDate(today.getDate() - (day === 0 ? 6 : day - 1));

  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const quarter = Math.floor(today.getMonth() / 3);
  const quarterStart = new Date(today.getFullYear(), quarter * 3, 1);
  const quarterEnd = new Date(today.getFullYear(), quarter * 3 + 3, 0);

  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth(); 
  const isOnOrAfterApril = currentMonth >= 3;
  const fyStartYear = isOnOrAfterApril ? currentYear : currentYear - 1;

  const thisFyStart = new Date(fyStartYear, 3, 1); 
  const thisFyEnd = today; 

  const prevFyStartYear = fyStartYear - 1;
  const prevFyStart = new Date(prevFyStartYear, 3, 1); 
  const prevFyEnd = new Date(fyStartYear, 3, 0); 

  switch (preset) {
    case "Today":
      return { start: startOfDay(today), end: endOfDay(today) };

    case "Yesterday": {
      const y = new Date();
      y.setDate(today.getDate() - 1);
      return { start: startOfDay(y), end: endOfDay(y) };
    }

    case "This Week":
      return { start: startOfDay(monday), end: endOfDay(today) };

    case "Previous Week": {
      const lastMonday = new Date(monday);
      lastMonday.setDate(lastMonday.getDate() - 7);

      const lastSunday = new Date(lastMonday);
      lastSunday.setDate(lastMonday.getDate() + 6);

      return { start: startOfDay(lastMonday), end: endOfDay(lastSunday) };
    }

    case "This Month":
      return { start: startOfDay(monthStart), end: endOfDay(monthEnd) };

    case "Previous Month": {
      const pmStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const pmEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: startOfDay(pmStart), end: endOfDay(pmEnd) };
    }

    case "This Quarter":
      return { start: startOfDay(quarterStart), end: endOfDay(quarterEnd) };

    case "Previous Quarter": {
      const prevQ = quarter - 1;
      const pqStart = new Date(today.getFullYear(), prevQ * 3, 1);
      const pqEnd = new Date(today.getFullYear(), prevQ * 3 + 3, 0);
      return { start: startOfDay(pqStart), end: endOfDay(pqEnd) };
    }

    case "This Year":
      return { start: startOfDay(thisFyStart), end: endOfDay(thisFyEnd) };

    case "Previous Year": {
      return { start: startOfDay(prevFyStart), end: endOfDay(prevFyEnd) };
    }

    default:
      return null;
  }
}

export default function DateFilter({ onDateChange, resetSignal }) {
  const [preset, setPreset] = useState("This Year");
  const [useCustom, setUseCustom] = useState(false);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  useEffect(() => {
    const range = getRangeForPreset("This Year");
    onDateChange({
      start: range.start,
      end: range.end,
      preset: "This Year",
      isCustom: false,
    });
  }, []);

  useEffect(() => {
    const range = getRangeForPreset("This Year");

    setPreset("This Year");
    setUseCustom(false);
    setCustomFrom("");
    setCustomTo("");

    onDateChange({
      start: range.start,
      end: range.end,
      preset: "This Year",
      isCustom: false,
    });

  }, [resetSignal]);

  const applyPreset = (p) => {
    if (p === "Custom") {
      setUseCustom(true);
      return;
    }

    const range = getRangeForPreset(p);
    if (!range) return;

    setPreset(p);
    setUseCustom(false);

    onDateChange({
      start: range.start,
      end: range.end,
      preset: p,
      isCustom: false,
    });

    handleClose();
  };

  const applyCustomRange = () => {
    if (!customFrom || !customTo) return;

    const start = startOfDay(new Date(customFrom));
    const end = endOfDay(new Date(customTo));

    setPreset("Custom");

    onDateChange({
      start,
      end,
      preset: "Custom",
      isCustom: true,
    });

    handleClose();
  };

  return (
    <Box>
      <Button
        variant="outlined"
        endIcon={<ArrowDropDown />}
        onClick={handleOpen}
        sx={{ borderColor: '#0053FF',color: '#0053FF' }}
      >
        {useCustom && customFrom && customTo
          ? `${customFrom} - ${customTo}`
          : preset}
      </Button>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose} >
        <Box sx={{ p: 1, minWidth: 260 }}>
          {DATE_PRESETS.map((p) => (
            <MenuItem
              key={p}
              onClick={() => applyPreset(p)}
              selected={p === preset && !useCustom}
              sx={{
                paddingX: '4px',
                height: "32px",
              }}
            >
              {p}
            </MenuItem>
          ))}

          {useCustom && (
            <Box sx={{ mt: 2 }}>
              <Box display="flex" gap={1} mb={2}>
                <TextField
                  size="small"
                  label="From"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="To"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  fullWidth
                />
              </Box>

              <Box display="flex" gap={1}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={applyCustomRange}
                >
                  Apply
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setUseCustom(false);
                    setCustomFrom("");
                    setCustomTo("");
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Menu>
    </Box>
  );
}
